import { Alert, Box, Button, CircularProgress, Typography } from '@mui/joy'
import React, { useCallback, useEffect, useState } from 'react'
import rateconFineTuningApi from './api'
import { RateconfViewer } from './components/RateconfViewer'
import { StatsPanel } from './components/StatsPanel'
import { ValidationForm } from './components/ValidationForm'
import { Rateconf, StatsResponse, ValidationRequest } from './types'

const RateconFineTuningPage: React.FC = () => {
    const [currentRateconf, setCurrentRateconf] = useState<Rateconf | null>(null)
    const [remainingCount, setRemainingCount] = useState<number>(0)
    const [stats, setStats] = useState<StatsResponse | null>(null)
    const [loading, setLoading] = useState<boolean>(true)
    const [validating, setValidating] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    const [successMessage, setSuccessMessage] = useState<string | null>(null)
    const [exporting, setExporting] = useState<boolean>(false)

    const fetchStats = useCallback(async () => {
        try {
            const statsData = await rateconFineTuningApi.getStats()
            setStats(statsData)
        } catch (err) {
            console.error('Failed to fetch stats:', err)
        }
    }, [])

    const fetchRandomRateconf = useCallback(async () => {
        setLoading(true)
        setError(null)
        setSuccessMessage(null)

        try {
            const response = await rateconFineTuningApi.getRandomRateconf()
            setCurrentRateconf(response.rateconf)
            setRemainingCount(response.remainingCount)
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch rateconf'
            setError(errorMessage)
            setCurrentRateconf(null)
        } finally {
            setLoading(false)
        }
    }, [])

    const handleValidation = async (request: ValidationRequest) => {
        setValidating(true)
        setError(null)
        setSuccessMessage(null)

        try {
            const response = await rateconFineTuningApi.validateRateconf(request)

            if (response.success) {
                setSuccessMessage(`Successfully ${request.status} the rateconf! Loading next document...`)

                // Refresh stats
                await fetchStats()

                // Load next rateconf after a short delay
                setTimeout(() => {
                    fetchRandomRateconf()
                }, 1500)
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to submit validation'
            setError(errorMessage)
            throw err
        } finally {
            setValidating(false)
        }
    }

    const handleExport = async () => {
        setExporting(true)
        setError(null)
        setSuccessMessage(null)

        try {
            const response = await rateconFineTuningApi.exportLabels({ format: 'jsonl' })

            if (response.success && response.downloadUrl) {
                setSuccessMessage('Export successful! Downloading...')
                // Open download URL in new tab
                window.open(response.downloadUrl, '_blank')
            } else {
                setError(response.message || 'Export failed')
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to export labels'
            setError(errorMessage)
        } finally {
            setExporting(false)
        }
    }

    useEffect(() => {
        fetchStats()
        fetchRandomRateconf()
    }, [fetchStats, fetchRandomRateconf])

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography level="h2">Ratecon Fine-Tuning Dataset Collection</Typography>

                <Button color="primary" variant="outlined" onClick={handleExport} loading={exporting} disabled={exporting || !stats || stats.labeled === 0}>
                    {exporting ? 'Exporting...' : 'Export Labeled Data'}
                </Button>
            </Box>

            <Typography level="body-md" sx={{ mb: 3, color: 'text.secondary' }}>
                Review and validate ratecon extractions to build a fine-tuning dataset. Accept correct extractions or reject and provide corrections for
                incorrect ones.
            </Typography>

            {/* Stats Panel */}
            <StatsPanel stats={stats} loading={loading && stats === null} />

            {/* Success/Error Messages */}
            {successMessage && (
                <Alert color="success" sx={{ mb: 3 }}>
                    {successMessage}
                </Alert>
            )}

            {error && (
                <Alert color="danger" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Loading State */}
            {loading && !currentRateconf && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    <CircularProgress size="lg" />
                </Box>
            )}

            {/* No More Documents */}
            {!loading && !currentRateconf && !error && (
                <Alert color="warning" sx={{ mt: 3 }}>
                    <Typography level="title-md">No More Documents</Typography>
                    <Typography level="body-sm">All documents have been labeled or no documents exist in the last 3 months. Great work!</Typography>
                </Alert>
            )}

            {/* Current Rateconf */}
            {currentRateconf && (
                <>
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                            <strong>Remaining documents:</strong> {remainingCount.toLocaleString()}
                        </Typography>
                    </Box>

                    <RateconfViewer
                        fileUrl={currentRateconf.fileUrl}
                        extraction={currentRateconf.rateConf}
                        projectName={currentRateconf.projectName}
                        emailDate={currentRateconf.emailDate}
                        emailSubject={currentRateconf.emailSubject}
                    />

                    <ValidationForm
                        rateconfId={currentRateconf._id}
                        projectName={currentRateconf.projectName}
                        documentId={currentRateconf.rateConf.documentId || ''}
                        fileUrl={currentRateconf.fileUrl}
                        originalExtraction={currentRateconf.rateConf}
                        onSubmit={handleValidation}
                        loading={validating}
                    />

                    {/* Skip Button */}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                        <Button variant="plain" onClick={fetchRandomRateconf} disabled={loading || validating}>
                            Skip to Next Random Document
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    )
}

export default RateconFineTuningPage
